import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PicklistField from 'core/components/validatedForm/DropdownField'
import Info from 'core/components/validatedForm/Info'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { getCurrentUser } from 'openstack/helpers'
import { getSingleVolume } from '../storage/volumes/actions'
import { rebuildVirtualMachine } from './actions'
import { convertToBytes, hasOtherUserVm } from './helpers'
import ImagePicklist from './ImagePicklist'

type VmVolumeDetails = {
  id?: string
  volume_id?: string
  volume_size?: number | string
  boot_index?: number
}

const isBootableVolume = (volume): boolean => {
  return String(volume?.bootable).toLowerCase() === 'true'
}

// Todo: Check into how to rebuild vms that used bootable volume
export default function RebuildVirtualMachineDialog({ rows: vms, onClose }) {
  const vm = vms?.[0]
  const classes = useStyles()
  const { params, getParamsUpdater, updateParams } = useParams<Record<string, any>>({
    imageId: null,
  })

  const ipAddresses = useMemo(() => {
    const networkKeys = Object.keys(vm?.addresses || {})
    const ips = networkKeys.map((key) => vm?.addresses?.[key].map((iface) => iface.addr)).flat()
    return ips
  }, [vm])

  const { update: rebuildVm, updating, error } = useUpdateAction(rebuildVirtualMachine)
  const [bootableImageDetails, setBootableImageDetails] = useState<{ id: string; name: string }>(
    null,
  )
  const [bootableVolumeSizeGb, setBootableVolumeSizeGb] = useState<number | null>(null)
  const [loadingVolumes, setLoadingVolumes] = useState(true)
  const [validationError, setValidationError] = useState<{ title?: string; message: string }>(null)

  const rootVolumeDetails = useMemo<VmVolumeDetails | null>(() => {
    const volumeDetailsEntries = Object.entries(
      (vm?.volumeDetails || {}) as Record<string, VmVolumeDetails>,
    )
    if (!volumeDetailsEntries.length) {
      return null
    }
    const rootVolume = volumeDetailsEntries.find(([, volumeDetails]) => {
      return volumeDetails?.boot_index === 0
    })
    return rootVolume?.[1] || null
  }, [vm?.volumeDetails])

  const rootVolumeSizeFromVmDetailsGb = useMemo(() => {
    const rootVolumeSize = Number(rootVolumeDetails?.volume_size)
    return Number.isFinite(rootVolumeSize) ? rootVolumeSize : null
  }, [rootVolumeDetails])

  const attachedVolumeIds = useMemo(() => {
    const volumeIdsFromVm = vm?.volumeIds || []
    const volumeIdsFromDetails = Object.values(
      (vm?.volumeDetails || {}) as Record<string, VmVolumeDetails>,
    )
      .map((details) => details?.volume_id || details?.id)
      .filter(Boolean)
    return Array.from(new Set([...volumeIdsFromVm, ...volumeIdsFromDetails]))
  }, [vm?.volumeIds, vm?.volumeDetails])

  const getVolumes = useCallback(async () => {
    if (!attachedVolumeIds.length) {
      setBootableImageDetails(null)
      setBootableVolumeSizeGb(null)
      setLoadingVolumes(false)
      return
    }
    try {
      setLoadingVolumes(true)
      const promises = attachedVolumeIds.map((id) => getSingleVolume(id))
      const responses = await Promise.allSettled(promises)
      const vols = responses
        .filter((response) => response.status === 'fulfilled')
        .map((response: any) => response.value)
      const rootVolumeId = rootVolumeDetails?.volume_id || rootVolumeDetails?.id
      const bootableVolume =
        (rootVolumeId && vols?.find((vol) => vol?.id === rootVolumeId)) ||
        vols?.find((vol) => isBootableVolume(vol)) ||
        null
      setBootableImageDetails({
        id: bootableVolume?.volume_image_metadata?.image_id,
        name: bootableVolume?.volume_image_metadata?.image_name,
      })
      const rootVolumeSize = Number(bootableVolume?.size)
      setBootableVolumeSizeGb(Number.isFinite(rootVolumeSize) ? rootVolumeSize : null)
    } catch (e) {
      console.log(e)
    } finally {
      setLoadingVolumes(false)
    }
  }, [attachedVolumeIds, rootVolumeDetails])

  useEffect(() => {
    getVolumes()
  }, [getVolumes])

  const preferredImageId = vm?.imageDetails?.id || bootableImageDetails?.id || null

  useEffect(() => {
    if (preferredImageId && !params.imageId) {
      updateParams({ imageId: preferredImageId })
    }
  }, [preferredImageId, params.imageId, updateParams])

  const submitForm = useCallback(async () => {
    const imageId = params.imageId || preferredImageId
    if (!imageId) {
      setValidationError({ title: '', message: 'Please select an image to rebuild this VM.' })
      return
    }

    setValidationError(null)
    const body = {
      rebuild: {
        imageRef: imageId,
      },
    }
    const { success } = await rebuildVm({
      id: vm.id,
      body,
    })
    if (success) onClose(true)
  }, [vm, params.imageId, preferredImageId, rebuildVm, onClose])

  const { role: userRole, userId } = getCurrentUser()

  const filteredImages = useCallback(
    (images) => {
      const flavorDiskInBytes = convertToBytes(vm?.flavor?.disk || 0, 'GB') || 0
      const bootableVolumeSizeInBytes =
        bootableVolumeSizeGb && bootableVolumeSizeGb > 0
          ? convertToBytes(bootableVolumeSizeGb, 'GB') || 0
          : 0
      const rootVolumeSizeFromVmDetailsInBytes =
        rootVolumeSizeFromVmDetailsGb && rootVolumeSizeFromVmDetailsGb > 0
          ? convertToBytes(rootVolumeSizeFromVmDetailsGb, 'GB') || 0
          : 0
      const maxSupportedDiskInBytes =
        bootableVolumeSizeInBytes || rootVolumeSizeFromVmDetailsInBytes || flavorDiskInBytes

      return images?.filter((image) => {
        if (image.status !== 'active' || image?.imageType !== 'image') {
          return false
        }

        if (maxSupportedDiskInBytes <= 0) {
          return true
        }

        const imageVirtualSize = Number(image?.virtual_size) || 0
        const imageMinDiskInBytes = convertToBytes(image?.min_disk || 0, 'GB') || 0
        const requiredImageSize = Math.max(imageVirtualSize, imageMinDiskInBytes)
        return requiredImageSize <= maxSupportedDiskInBytes
      })
    },
    [vm?.flavor?.disk, bootableVolumeSizeGb, rootVolumeSizeFromVmDetailsGb],
  )

  return (
    <ModalForm
      open
      panel="dialog"
      title={`Rebuild VM`}
      onSubmit={submitForm}
      onClose={onClose}
      submitting={updating}
      error={validationError || error}
      submitTitle={`Rebuild VM`}
      maxWidth={800}
      loading={!vm?.imageDetails?.id && loadingVolumes}
    >
      <div className={classes.body}>
        {userRole === 'member' && hasOtherUserVm({ vms, userId }) && (
          <Info error>NOTE: You are performing an operation on another user's VM.</Info>
        )}
        <Text variant="body2">
          You are about to rebuild the VM <b>{vm?.name || vm?.id}</b>
        </Text>
        <Text variant="body2">
          Rebuilding a VM will redeploy the selected VM based on the VM's image and existing flavor,
          network, and storage configuration. This operation will recreate the root disk of the VM.
        </Text>
        <Text variant="body2">
          <b>IMPORTANT:</b> This will delete all the data on the VM and replace it with the
          specified image. Only the VM ID, flavor and IP address (fixed and floating) will remain
          the same.
        </Text>
        <div className={classes.properties}>
          {(vm?.imageDetails?.name || vm?.imageDetails?.id || bootableImageDetails?.name) && (
            <>
              <Text variant="caption1">Current Image:</Text>
              <Text variant="body2">
                {vm?.imageDetails?.name ||
                  vm?.imageDetails?.id ||
                  bootableImageDetails?.name ||
                  bootableImageDetails?.id ||
                  'unknown'}
              </Text>
            </>
          )}
          <PicklistField
            DropdownComponent={ImagePicklist}
            id="image"
            label="Select Image"
            filterFn={(images) => filteredImages(images)}
            onChange={getParamsUpdater('imageId')}
            value={params.imageId}
            className={classes.imagePicklist}
            required
          />

          <Text variant="caption1">Flavor:</Text>
          <Text variant="body2">
            {vm?.flavor?.original_name || 'unknown'}{' '}
            {vm?.flavor
              ? `(${vm?.flavor?.vcpus} vCPUs, ${vm?.flavor?.ram} RAM (MB), ${vm?.flavor?.disk} Disk (GB))`
              : ''}
          </Text>
          <Text variant="caption1">IP Addresses:</Text>
          <Text variant="body2">{ipAddresses.join(', ')}</Text>
        </div>
      </div>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  body: {
    display: 'grid',
    gap: 12,
  },
  properties: {
    display: 'grid',
    gridTemplateColumns: 'auto',
    gap: '8px 16px',
    '& .dropdown-container > label, & .dropdown-container > div': {
      marginLeft: 0,
    },
  },
}))
